/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';

import CopyDateInputs from './copyWorkoutModal/CopyDateInputs';
import CopyDateModeToggle from './copyWorkoutModal/CopyDateModeToggle';
import CopyDaySelector from './copyWorkoutModal/CopyDaySelector';
import CopyOptionsSection from './copyWorkoutModal/CopyOptionsSection';
import CopyProgramDayGrid from './copyWorkoutModal/CopyProgramDayGrid';
import CopySection, { HelperText } from './copyWorkoutModal/CopySection';
import CopyTargetPicker from './copyWorkoutModal/CopyTargetPicker';
import ModalCtaButton from '../components/presentational/ModalCtaButton';
import {
  countDaysInclusive,
  countSelectedWeekdaysInRange,
  formatSelectedWeekdays,
  formatShortDate,
  pluralize,
} from './copyWorkoutModal/copyDateUtils';
import { FOCUSABLE_SELECTOR, handleCheckboxEnter } from '../../../shared/utils/focusHelpers';
import { subscribeKeyboardNav } from '../../../shared/utils/keyboardNavMode';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import { copyWorkoutItems } from '../ducks/calendarActions';

const MAX_COPY_RANGE_DAYS = 365;

const PLACEMENT_OPTIONS = [
  { value: 'bottom', label: 'Bottom of workout' },
  { value: 'top', label: 'Top of workout' },
];

const EMPTY_SELECTED_DAYS = {
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
};

const ModalContainer = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
`;

/* The right padding keeps a long title clear of the absolutely positioned
   close button. */
const HeaderRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
  padding-right: 32px;
`;

const Title = styled('h2')`
  margin: 0;
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CloseButton = styled('button')`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }

  /* the global stylesheet removes button focus outlines, so keyboard users
     need an explicit focus treatment */
  &:focus-visible {
    border-radius: 4px;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.hover};
  }
`;

const SelectedItemsCount = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const ErrorText = styled('div')`
  margin-top: 8px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textError};
`;

const DaysBlock = styled('div')`
  margin-top: 16px;
`;

const DaysHeaderRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  margin-bottom: 8px;
  font-family: 'Nunito Sans';
  font-size: 14px;
`;

const DaysLabel = styled('span')`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const DaysSummary = styled('span')`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SelectedItemsWarning = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.warningSurface};
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.warningText};
`;

const WarningIconWrapper = styled('div')`
  min-width: 18px;
`;

const FooterContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 24px;
`;

/** programItems is an object keyed by index whose values are program days. */
const buildWeekDayStructure = (programItems) => {
  if (!programItems || typeof programItems !== 'object') return [];

  const weekMap = {};
  Object.values(programItems).forEach((item) => {
    const weekNum = item.weekNumber;
    if (!weekMap[weekNum]) {
      weekMap[weekNum] = { weekNumber: weekNum, days: [] };
    }
    weekMap[weekNum].days.push({
      dayNumber: item.dayNumber,
      dayInWeek: item.dayInWeek,
      exercises: item.exercises || [],
      title: item.title || `Day ${item.dayNumber + 1}`,
    });
  });

  return Object.values(weekMap).sort((a, b) => a.weekNumber - b.weekNumber);
};

const WarningIcon = () => (
  <WarningIconWrapper>
    <IcomoonReact color='#F59E0B' icon='warning' iconSet={iconSet} size={18} />
  </WarningIconWrapper>
);

const CopyWorkoutModal = ({ closeModal, modalData, setIsCloseLocked }) => {
  const preSavedItemAssignedIds = Object.values(modalData?.preSelectedItems || {})
    .map((item) => item?.assignedId)
    .filter((id) => typeof id === 'string');

  const savedItemAssignedIds = Object.values(modalData?.preSelectedItems || {})
    .map((item) => item?.assignedId)
    .filter((id) => typeof id === 'number');

  const dispatch = useDispatch();
  const { colors } = useTheme();

  const getDateFromPreSelectedItems = () => {
    if (modalData?.preSelectedItems) {
      const firstItem = Object.values(modalData.preSelectedItems)[0];
      return firstItem?.date; // "2025-10-14" format
    }
    return null;
  };

  const initializeDatesFromModalData = () => {
    if (modalData?.preSelectedItems) {
      const preSelectedDate = getDateFromPreSelectedItems();
      if (preSelectedDate) {
        return {
          dateMode: 'single',
          startDate: moment(preSelectedDate).toDate(),
          endDate: moment(preSelectedDate).toDate(),
          startDateString: preSelectedDate,
          endDateString: preSelectedDate,
        };
      }
    }

    if (modalData?.isFromWeekView && modalData?.weekStartDate && modalData?.weekEndDate) {
      return {
        dateMode: 'range',
        startDate: modalData.weekStartDate,
        endDate: modalData.weekEndDate,
        startDateString: moment(modalData.weekStartDate).format('YYYY-MM-DD'),
        endDateString: moment(modalData.weekEndDate).format('YYYY-MM-DD'),
      };
    }

    const defaultDate = modalData?.currentViewedDate
      ? moment(modalData.currentViewedDate).toDate()
      : new Date();

    return {
      dateMode: 'single',
      startDate: defaultDate,
      endDate: defaultDate,
      startDateString: moment(defaultDate).format('YYYY-MM-DD'),
      endDateString: moment(defaultDate).format('YYYY-MM-DD'),
    };
  };

  const initialDates = initializeDatesFromModalData();

  /* "secondSlide" names the paste-destination dates. They keep their original
     names because prepareSubmitData, which builds the API payload, reads them. */
  const [dateMode, setDateMode] = useState(initialDates.dateMode);
  const [secondSlideDateMode, setSecondSlideDateMode] = useState('single');
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);
  const [startDateString, setStartDateString] = useState(initialDates.startDateString);
  const [endDateString, setEndDateString] = useState(initialDates.endDateString);
  const [selectedDays, setSelectedDays] = useState(EMPTY_SELECTED_DAYS);

  const defaultDate = modalData?.currentViewedDate
    ? moment(modalData.currentViewedDate).toDate()
    : new Date();

  const [secondSlideStartDate, setSecondSlideStartDate] = useState(defaultDate);
  const [secondSlideEndDate, setSecondSlideEndDate] = useState(defaultDate);
  const [secondSlideStartDateString, setSecondSlideStartDateString] = useState(moment(defaultDate).format('YYYY-MM-DD'));
  const [secondSlideEndDateString, setSecondSlideEndDateString] = useState(moment(defaultDate).format('YYYY-MM-DD'));

  const [contentOptions, setContentOptions] = useState({
    circuits: true,
    health: true,
    notes: true,
    rest: true,
    warmUps: true,
    cooldowns: true,
    questionnaires: true,
    sessions: true,
    videos: true,
    prescribedLoads: true,
    workoutTitles: false,
    clearCurrentWorkouts: false,
  });

  const [additionalOptions, setAdditionalOptions] = useState({
    circuits2: true,
    health2: true,
    notes2: true,
  });

  const [checkedCalendars, setCheckedCalendars] = useState(new Set());
  const [checkedAthletes, setCheckedAthletes] = useState(new Set());

  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars);
  const calendarSelectAthletes = useSelector((state) => state.calendar.data.calendarSelectAthletes);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const isParentCalendarsLoading = useSelector(
    (state) => state.calendar.ui.isParentCalendarsLoading,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const programName = useSelector((state) => state.calendar.data.programName);
  const nonDateBasedProgramItemsProgramId = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItemsProgramId,
  );

  const getWeekDayFromPreSelectedItems = () => {
    if (modalData?.preSelectedItems && programType === 'free-form-program') {
      if (modalData.sourceDayNumber !== undefined && modalData.sourceWeekNumber !== undefined) {
        return {
          weekNumber: modalData.sourceWeekNumber,
          dayNumber: modalData.sourceDayNumber,
        };
      }

      const firstItem = Object.values(modalData?.preSelectedItems)[0];
      if (firstItem?.weekNumber !== undefined && firstItem?.dayNumber !== undefined) {
        return {
          weekNumber: firstItem.weekNumber,
          dayNumber: firstItem.dayNumber,
        };
      }
    }
    return null;
  };

  /* The target is either calendars or athlete calendars, never both - the API
     rejects a request that names both. */
  const [rightToggled, setRightToggled] = useState(
    !!(selectedReduxAthlete && Object.keys(selectedReduxAthlete).length),
  );
  const [placementOption, setPlacementOption] = useState(PLACEMENT_OPTIONS[0]);
  const [clearExistingWorkouts, setClearExistingWorkouts] = useState(false);
  const [selectedProgramDays, setSelectedProgramDays] = useState(new Set());
  const [pasteDestinationDays, setPasteDestinationDays] = useState(new Set());

  /* The date fields and the placement dropdown must only show a focus ring for
     keyboard users, and neither :focus-within nor :focus-visible can tell mouse
     from keyboard on an input - so the tracked modality is mirrored onto the
     container for CSS to key off. Written through the ref rather than state
     because it fires on every keystroke and click. */
  const modalContainerRef = useRef(null);
  /* Only one date picker can be open at a time, so a single flag covers all of them. */
  const escapeBelongsToDatePickerRef = useRef(false);
  /* The modal stays mounted through its 250ms close animation, so a double click
     would dispatch the copy twice. The ref stops a second click that lands before
     the disabled state has rendered. */
  const hasSubmittedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [panelFocusRequest, setPanelFocusRequest] = useState(0);
  const [panelFocusTargetId, setPanelFocusTargetId] = useState(null);

  const isProgramLibrary = programType === 'free-form-program';
  const hasSelectedItems = !!modalData?.preSelectedItems
    && Object.keys(modalData.preSelectedItems).length > 0;
  const shouldSkipFirstSlide = modalData?.skipFirstSlide
    || (isProgramLibrary && modalData?.preSelectedItems);
  const isFromReadOnly = !isProgramLibrary && !!modalData?.skipFirstSlide;
  const showTargetPicker = !(isProgramLibrary && modalData?.preSelectedItems);

  /* Start with the calendar being worked in as the target - most copies stay
     inside it, and its pill's x clears it in one click. parentCalendars can
     still be loading when the modal opens, so this runs once the current
     calendar shows up, and never over a target the coach already picked. Ids
     are matched as strings: the redux calendar's id isn't always a number. */
  const hasSeededTargetRef = useRef(false);
  useEffect(() => {
    if (hasSeededTargetRef.current || !showTargetPicker) return;
    if (checkedCalendars.size || checkedAthletes.size) {
      hasSeededTargetRef.current = true;
      return;
    }
    if (rightToggled) {
      const currentAthlete = selectedReduxAthlete?.id != null && (calendarSelectAthletes || [])
        .find((athlete) => String(athlete.id) === String(selectedReduxAthlete.id));
      if (!currentAthlete) return;
      hasSeededTargetRef.current = true;
      setCheckedAthletes(new Set([currentAthlete.id]));
      return;
    }
    if (selectedReduxCalendar?.id == null) return;
    const currentCalendar = (parentCalendars || [])
      .flatMap((calendar) => [calendar, ...(calendar.childCalendars || [])])
      .find((calendar) => !calendar.archived
        && String(calendar.id) === String(selectedReduxCalendar.id));
    if (!currentCalendar) return;
    hasSeededTargetRef.current = true;
    setCheckedCalendars(new Set([currentCalendar.id]));
  }, [
    showTargetPicker,
    rightToggled,
    parentCalendars,
    calendarSelectAthletes,
    selectedReduxCalendar,
    selectedReduxAthlete,
    checkedCalendars,
    checkedAthletes,
  ]);

  const isSourceRange = !isProgramLibrary && dateMode === 'range';
  const effectiveTargetMode = isSourceRange ? 'single' : secondSlideDateMode;
  const programWeeks = isProgramLibrary ? buildWeekDayStructure(nonDateBasedProgramItems) : [];

  const totalDaysCount = dateMode === 'single' ? 1 : countDaysInclusive(startDate, endDate);
  const dateRangeError = totalDaysCount > MAX_COPY_RANGE_DAYS
    ? `You cannot copy a date range longer than ${MAX_COPY_RANGE_DAYS} days`
    : null;

  /* A date field can legitimately be empty (the user cleared it), but a copy
     needs one. The Program Library has no date inputs at all. */
  const isMissingCopyDate = !isProgramLibrary
    && (!startDateString || (dateMode === 'range' && !endDateString));
  const isMissingTargetDate = !isProgramLibrary
    && (!secondSlideStartDateString
      || (effectiveTargetMode === 'range' && !secondSlideEndDateString));
  const isPasteDisabled = isMissingTargetDate
    || (effectiveTargetMode === 'range' && !Object.values(selectedDays).some(Boolean));
  const isTargetMissing = showTargetPicker
    && (rightToggled ? checkedAthletes.size < 1 : checkedCalendars.size < 1);

  const getTargetDayCount = () => {
    if (isProgramLibrary) {
      return pasteDestinationDays.size;
    }
    if (isSourceRange) {
      return totalDaysCount;
    }
    if (effectiveTargetMode === 'range') {
      return countSelectedWeekdaysInRange(secondSlideStartDate, secondSlideEndDate, selectedDays);
    }
    return secondSlideStartDateString ? 1 : 0;
  };
  const targetDayCount = getTargetDayCount();

  const isSubmitDisabled = targetDayCount < 1
    || isTargetMissing
    || (savedItemAssignedIds.length < 1 && !!modalData?.skipFirstSlide)
    || (!isProgramLibrary && isPasteDisabled)
    || (!isFromReadOnly && !isProgramLibrary && (isMissingCopyDate || !!dateRangeError));

  const selectedItemCount = Object.keys(modalData?.preSelectedItems || {}).length;

  /* A source range is pasted from one start date, so "Copy to 3 days" read as
     three days landing on one - it names what is copied instead. */
  const getSubmitLabel = () => {
    if (targetDayCount < 1) {
      return 'Copy';
    }
    const days = pluralize(targetDayCount, 'day');
    return isSourceRange ? `Copy ${days}` : `Copy to ${days}`;
  };

  const getTitle = () => {
    if (isFromReadOnly) {
      return 'Copy';
    }
    return isSourceRange ? 'Copy workouts' : 'Copy workout';
  };

  /* When items were picked by checkbox the count already sits in the Workout
     items section, so the header doesn't repeat it. */
  const getSubtitle = () => {
    const itemText = selectedItemCount ? pluralize(selectedItemCount, 'item') : '';
    if (isProgramLibrary) {
      return [programName, itemText].filter(Boolean).join(' · ');
    }
    return isFromReadOnly ? '' : itemText;
  };

  /* Moves focus to the first control after a date picker, so finishing with one
     date hands the user straight to the next field. The footer is included: the
     day cell consumes the Enter keydown, so reaching the submit button can never
     submit on the same press. */
  const focusFieldAfter = (wrapper) => {
    const container = modalContainerRef.current;
    if (!container) {
      return;
    }
    const next = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .find((el) => !wrapper.contains(el)
        // eslint-disable-next-line no-bitwise
        && (wrapper.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
    next?.focus();
  };

  /* react-datepicker calls preventDefault on Escape but never stopPropagation,
     and its popper renders inside the react-modal content element - so an Escape
     meant for the calendar would also close the whole modal, discarding the
     user's setup. "Is the calendar open" is snapshotted in the capture phase,
     before react-datepicker closes it, and propagation is stopped on the way
     back up. Reading it in the bubble handler alone would depend on React's
     batching timing. */
  const handleDatePickerKeyDownCapture = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      escapeBelongsToDatePickerRef.current = !!e.currentTarget.querySelector('.react-datepicker-popper');
    }
  };

  const handleDatePickerKeyDown = (e) => {
    if (!escapeBelongsToDatePickerRef.current) {
      return;
    }
    if (e.key === 'Escape') {
      escapeBelongsToDatePickerRef.current = false;
      e.stopPropagation();
      focusFieldAfter(e.currentTarget);
    }
    if (e.key === 'Enter') {
      escapeBelongsToDatePickerRef.current = false;
      focusFieldAfter(e.currentTarget);
    }
  };

  /* A source range always pastes from a single start date, so switching the
     source to a range resets the destination to single. */
  const handleFromModeSelect = (mode) => {
    setDateMode(mode);
    if (mode === 'range') {
      setSecondSlideDateMode('single');
    }
  };

  /* The mode switch swaps the panel's contents (one picker vs. two), so focus
     has to wait for the next render - hence a request counter. */
  const requestPanelFocus = (panelId) => {
    setPanelFocusTargetId(panelId);
    setPanelFocusRequest((count) => count + 1);
  };

  const handleFromModeEnter = (mode, panelId) => {
    handleFromModeSelect(mode);
    requestPanelFocus(panelId);
  };

  const handleTargetModeEnter = (mode, panelId) => {
    setSecondSlideDateMode(mode);
    requestPanelFocus(panelId);
  };

  /* Clearing the input calls these with null. Formatting that would store the
     literal string 'Invalid date', which is truthy and crashes react-datepicker
     on the next render, so an empty field has to stay genuinely empty. */
  const handleStartDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setStartDate(null);
      setStartDateString('');
      return;
    }
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));

    if (moment(endDate).isBefore(momentDate, 'day')) {
      setEndDate(momentDate.toDate());
      setEndDateString(momentDate.format('YYYY-MM-DD'));
    }
  };

  const handleEndDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setEndDate(null);
      setEndDateString('');
      return;
    }
    setEndDate(momentDate.toDate());
    setEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  const handleSecondSlideStartDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setSecondSlideStartDate(null);
      setSecondSlideStartDateString('');
      return;
    }
    setSecondSlideStartDate(momentDate.toDate());
    setSecondSlideStartDateString(momentDate.format('YYYY-MM-DD'));

    if (moment(secondSlideEndDate).isBefore(momentDate, 'day')) {
      setSecondSlideEndDate(momentDate.toDate());
      setSecondSlideEndDateString(momentDate.format('YYYY-MM-DD'));
    }
  };

  const handleSecondSlideEndDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setSecondSlideEndDate(null);
      setSecondSlideEndDateString('');
      return;
    }
    setSecondSlideEndDate(momentDate.toDate());
    setSecondSlideEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const toggleContentOption = (option) => {
    setContentOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const toggleAdditionalOption = (option) => {
    setAdditionalOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const handleToggleClearExisting = () => {
    setClearExistingWorkouts((prev) => !prev);
  };

  const toggleProgramDay = (weekNumber, dayNumber, currentSelections) => {
    const key = `${weekNumber}-${dayNumber}`;
    // Program days are single-select: clicking the selected day clears it.
    if (currentSelections.has(key)) {
      return new Set();
    }
    return new Set([key]);
  };

  const handleSourceProgramDayToggle = (weekNumber, dayNumber) => {
    setSelectedProgramDays((prev) => toggleProgramDay(weekNumber, dayNumber, prev));
  };

  const handlePasteProgramDayToggle = (weekNumber, dayNumber) => {
    setPasteDestinationDays((prev) => toggleProgramDay(weekNumber, dayNumber, prev));
  };

  const prepareSubmitData = () => {
    // Get array of selected days of week (0-6, where 0 is Sunday)
    const getSelectedDays = () => {
      const dayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      return Object.entries(selectedDays)
        .filter(([, isSelected]) => isSelected)
        .map(([day]) => dayMap[day]);
    };

    // Program Library with pre-selected items - uses new endpoint
    if (programType === 'free-form-program' && modalData?.preSelectedItems) {
      // Extract source assignedIds from pre-selected items
      // Filter out presaved items (which have string IDs instead of numbers)
      const sourceAssignedIds = Object.values(modalData?.preSelectedItems)
        .map((item) => item.assignedId)
        .filter((id) => id !== undefined && id !== null && typeof id === 'number');

      // Get week/day info from first pre-selected item to calculate sourceDayNumber
      const weekDayInfo = getWeekDayFromPreSelectedItems();

      const sourceDayNumber = weekDayInfo
        ? weekDayInfo.dayNumber
        : 0;

      // Get target day info from first selected paste destination
      const firstTargetKey = Array.from(pasteDestinationDays)[0];
      let targetDayNumber = 0;
      if (firstTargetKey) {
        const [, dayNum] = firstTargetKey.split('-').map(Number);
        targetDayNumber = dayNum;
      }

      const payload = {
        clearExisting: clearExistingWorkouts,
        copyType: 'individual-assignments',
        includePrescribedLoads: contentOptions.prescribedLoads,
        includeWorkoutInfo: contentOptions.workoutTitles,
        sourceAssignedIds,
        sourceDayNumber,
        targetDayNumber,
      };

      // Add targetOrderLocation top when clearExistingWorkouts is checked
      if (clearExistingWorkouts) {
        payload.targetOrderLocation = 'top';
      }

      // otherwise use value
      if (!clearExistingWorkouts) {
        payload.targetOrderLocation = placementOption.value;
      }

      return payload;
    }

    // Program Library without pre-selected items - uses original endpoint
    if (programType === 'free-form-program') {
      // Add exerTypes for content filtering
      const typeMap = {
        circuits: 'L',
        health: 'T',
        notes: 'R',
        rest: 'RT',
        warmUps: 'W',
        cooldowns: 'CD',
        questionnaires: 'S',
        sessions: 'BR',
        videos: 'V',
      };

      const exerTypes = [];
      Object.entries(contentOptions).forEach(([key, value]) => {
        if (value && typeMap[key]) {
          exerTypes.push(typeMap[key]);
        }
      });

      Object.entries(additionalOptions).forEach(([key, value]) => {
        if (value) {
          if (key === 'circuits2') exerTypes.push('C');
          if (key === 'health2') exerTypes.push('H');
          if (key === 'notes2') exerTypes.push('N');
        }
      });

      // For program library, use date-based parameters matching API schema
      const payload = {
        clearExisting: clearExistingWorkouts,
        copyType: dateMode === 'range'
          ? 'range'
          : (secondSlideDateMode === 'range' ? 'multiple' : 'single'),
        exerTypes: [...new Set(exerTypes)],
        includePrescribedLoads: contentOptions.prescribedLoads,
        includeWorkoutInfo: contentOptions.workoutTitles,
        sourceDateStart: moment(startDate).format('YYYY-MM-DD'),
        sourceDateEnd: moment(dateMode === 'single' ? startDate : endDate).format('YYYY-MM-DD'),
        targetDateStart: moment(secondSlideStartDate).format('YYYY-MM-DD'),
        targetDateEnd: moment(secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideEndDate).format('YYYY-MM-DD'),
      };

      // Only add targetOrderLocation if we're not clearing existing workouts
      if (!clearExistingWorkouts) {
        payload.targetOrderLocation = placementOption.value;
      }

      // Add source information based on current Redux selection
      if (selectedReduxCalendar?.id) {
        payload.sourceCalendarId = parseInt(selectedReduxCalendar.id, 10);
      }
      if (selectedReduxAthlete?.userId) {
        payload.sourceUserId = parseInt(selectedReduxAthlete.userId, 10);
      }

      // Add target information based on what's selected in slide 3
      if (!rightToggled) {
        // Calendar mode - use selected calendars from checkboxes
        const selectedCalendarIds = Array.from(checkedCalendars);
        if (selectedCalendarIds.length > 0) {
          payload.targetCalendarIds = selectedCalendarIds;
        }
      } else {
        // Athlete mode - use selected athletes from checkboxes
        const selectedAthleteIds = Array.from(checkedAthletes);
        if (selectedAthleteIds.length > 0) {
          payload.targetUserIds = selectedAthleteIds;
        }
      }

      return payload;
    }

    // Calendar mode with pre-selected items - uses assignment-based endpoint
    if (modalData?.preSelectedItems && Object.keys(modalData?.preSelectedItems).length > 0) {
      // Extract source assignedIds from pre-selected items
      // Filter out presaved items (which have string IDs instead of numbers)
      const sourceAssignedIds = Object.values(modalData?.preSelectedItems)
        .map((item) => item.assignedId)
        .filter((id) => id !== undefined && id !== null && typeof id === 'number');

      // For calendar mode, we don't have day numbers, so we use dates instead
      // The backend will handle the date-to-day conversion
      const targetStartDate = secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideStartDate;
      const targetEndDate = dateMode === 'range'
        ? moment(secondSlideStartDate).add(moment(endDate).diff(moment(startDate), 'days'), 'days').toDate()
        : (secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideEndDate);

      const payload = {
        clearExisting: clearExistingWorkouts,
        copyType: 'individual-assignments',
        includePrescribedLoads: contentOptions.prescribedLoads,
        includeWorkoutInfo: contentOptions.workoutTitles,
        sourceAssignedIds,
        sourceDateStart: moment(startDate).format('YYYY-MM-DD'),
        sourceDateEnd: moment(endDate).format('YYYY-MM-DD'),
        targetDateStart: moment(targetStartDate).format('YYYY-MM-DD'),
        targetDateEnd: targetEndDate ? moment(targetEndDate).format('YYYY-MM-DD') : '',
        targetDays: secondSlideDateMode === 'range' ? getSelectedDays() : [],
      };

      // Only add targetOrderLocation if we're not clearing existing workouts
      if (!clearExistingWorkouts) {
        payload.targetOrderLocation = placementOption.value;
      }

      // Add source information based on current Redux selection
      if (selectedReduxCalendar?.id) {
        payload.sourceCalendarId = parseInt(selectedReduxCalendar.id, 10);
      }
      if (selectedReduxAthlete?.userId) {
        payload.sourceUserId = parseInt(selectedReduxAthlete.userId, 10);
      }

      // Add target information based on what's selected in slide 3
      if (!rightToggled) {
        // Calendar mode - use selected calendars from checkboxes
        const selectedCalendarIds = Array.from(checkedCalendars);
        if (selectedCalendarIds.length > 0) {
          payload.targetCalendarIds = selectedCalendarIds;
        }
      } else {
        // Athlete mode - use selected athletes from checkboxes
        const selectedAthleteIds = Array.from(checkedAthletes);
        if (selectedAthleteIds.length > 0) {
          payload.targetUserIds = selectedAthleteIds;
        }
      }

      return payload;
    }

    // Original Calendar mode
    // Map content options to exerTypes codes based on API documentation
    const mapContentOptionsToExerTypes = () => {
      const typeMap = {
        circuits: 'L', // Lifts (based on UI label)
        health: 'T', // Tags (based on UI label)
        notes: 'R', // SAQ+C (based on UI label)
        rest: 'RT', // Rest (based on UI label)
        warmUps: 'W', // Warmup
        cooldowns: 'CD', // Cooldown
        questionnaires: 'S', // Progression
        sessions: 'BR', // Session Break
        videos: 'V', // Video
      };

      const exerTypes = [];

      // Map selected content options to exercise types
      Object.entries(contentOptions).forEach(([key, value]) => {
        if (value && typeMap[key]) {
          exerTypes.push(typeMap[key]);
        }
      });

      // Map additional options to exercise types
      Object.entries(additionalOptions).forEach(([key, value]) => {
        if (value) {
          if (key === 'circuits2') exerTypes.push('C'); // Circuits
          if (key === 'health2') exerTypes.push('H'); // Health
          if (key === 'notes2') exerTypes.push('N'); // Notes
        }
      });

      return [...new Set(exerTypes)]; // Remove duplicates
    };

    // Get array of selected calendar IDs from checkboxes
    const getSelectedCalendarIds = () => Array.from(checkedCalendars);

    // Get array of selected athlete IDs from checkboxes (convert to user IDs for API)
    const getSelectedAthleteIds = () => Array.from(checkedAthletes);

    // Format date in YYYY-MM-DD format
    const formatDate = (date) => moment(date).format('YYYY-MM-DD');

    // Determine copy type based on whether we have pre-selected items and date modes
    let copyType = 'single';

    // Check if we have pre-selected items (individual assignments)
    const hasPreSelectedItems = modalData?.preSelectedItems
      && Object.keys(modalData?.preSelectedItems).length > 0;

    if (hasPreSelectedItems) {
      // When copying specific selected assignments, use 'individual-assignments' type
      copyType = 'individual-assignments';
    } else if (dateMode === 'range' && secondSlideDateMode === 'single') {
      copyType = 'range'; // Range to single date
    } else if (dateMode === 'range' && secondSlideDateMode === 'range') {
      copyType = 'range'; // Range to range
    } else if (dateMode === 'single' && secondSlideDateMode === 'range') {
      copyType = 'multiple'; // Single to range
    }

    // Use second slide dates for target dates
    const targetStartDate = secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideStartDate;
    // Calculate targetEndDate based on source range when range is selected on first slide
    const targetEndDate = dateMode === 'range'
      ? moment(secondSlideStartDate).add(moment(endDate).diff(moment(startDate), 'days'), 'days').toDate()
      : (secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideEndDate);

    // Build the request payload matching the expected API structure
    const payload = {
      clearExisting: clearExistingWorkouts,
      copyType,
      exerTypes: mapContentOptionsToExerTypes(), // Always required
      includePrescribedLoads: contentOptions.prescribedLoads,
      includeWorkoutInfo: contentOptions.workoutTitles,
      sourceDateEnd: formatDate(dateMode === 'single' ? startDate : endDate),
      sourceDateStart: formatDate(startDate),
      targetDateStart: formatDate(targetStartDate),
      targetDateEnd: targetEndDate ? formatDate(targetEndDate) : '',
      targetDays: (copyType === 'multiple' || (copyType === 'individual-assignments' && secondSlideDateMode === 'range'))
        ? getSelectedDays()
        : [],
    };

    // Only add targetOrderLocation if we're not clearing existing workouts
    if (!clearExistingWorkouts) {
      payload.targetOrderLocation = placementOption.value;
    }

    // Add source information based on current Redux selection
    if (selectedReduxCalendar?.id) {
      payload.sourceCalendarId = parseInt(selectedReduxCalendar.id, 10);
    }
    if (selectedReduxAthlete?.userId) {
      payload.sourceUserId = parseInt(selectedReduxAthlete.userId, 10);
    }

    // Add target information based on what's selected in slide 3
    if (!rightToggled) {
      // Calendar mode - use selected calendars from checkboxes
      const selectedCalendarIds = getSelectedCalendarIds();
      if (selectedCalendarIds.length > 0) {
        payload.targetCalendarIds = selectedCalendarIds;
      }
    } else {
      // Athlete mode - use selected athletes from checkboxes
      const selectedAthleteIds = getSelectedAthleteIds();
      if (selectedAthleteIds.length > 0) {
        payload.targetUserIds = selectedAthleteIds;
      }
    }

    return payload;
  };

  // Handle final submit
  const handleFinish = () => {
    // Prepare the data for submission
    const requestData = prepareSubmitData();

    // Check if this is the new free-form-program with pre-selected items flow
    const isNewProgramLibraryFlow = programType === 'free-form-program'
      && modalData?.preSelectedItems;

    if (isNewProgramLibraryFlow) {
      // Use new endpoint for program library with pre-selected items
      dispatch(copyWorkoutItems(
        currentUser.accountCode,
        requestData,
        nonDateBasedProgramItemsProgramId,
      ));
    } else {
      // Use existing endpoint
      dispatch(copyWorkoutItems(currentUser.accountCode, requestData));
    }

    // Close the modal
    closeModal();
  };

  const handleSubmitClick = () => {
    if (hasSubmittedRef.current) {
      return;
    }
    hasSubmittedRef.current = true;
    setIsSubmitting(true);
    handleFinish();
  };

  useEffect(() => subscribeKeyboardNav((usingKeyboard) => {
    if (modalContainerRef.current) {
      modalContainerRef.current.dataset.navMode = usingKeyboard ? 'keyboard' : 'mouse';
    }
  }), []);

  useEffect(() => {
    if (!panelFocusRequest || !panelFocusTargetId) {
      return;
    }
    document.getElementById(panelFocusTargetId)?.querySelector(FOCUSABLE_SELECTOR)?.focus();
  }, [panelFocusRequest, panelFocusTargetId]);

  const renderFromSection = () => {
    if (isProgramLibrary) {
      return (
        <CopySection id='copy-section-from' title='From'>
          <CopyProgramDayGrid
            idPrefix='copy-source-day'
            onToggleDay={handleSourceProgramDayToggle}
            selectedKeys={selectedProgramDays}
            weeks={programWeeks}
          />
        </CopySection>
      );
    }

    if (isFromReadOnly) {
      return (
        <CopySection id='copy-section-from' title='Workout items'>
          <SelectedItemsCount>
            {`${pluralize(selectedItemCount, 'workout item')} selected`}
          </SelectedItemsCount>
        </CopySection>
      );
    }

    return (
      <CopySection
        headerControl={(
          <CopyDateModeToggle
            ariaLabel='Copy date mode'
            idPrefix='copy-range-tab'
            mode={dateMode}
            onEnterSelect={handleFromModeEnter}
            onSelect={handleFromModeSelect}
            panelId='copy-range-date-section'
          />
        )}
        id='copy-section-from'
        title='From'
      >
        <CopyDateInputs
          endDateString={endDateString}
          labelledBy={`copy-range-tab-${dateMode}`}
          mode={dateMode}
          onEndChange={handleEndDateChange}
          onPickerKeyDown={handleDatePickerKeyDown}
          onPickerKeyDownCapture={handleDatePickerKeyDownCapture}
          onStartChange={handleStartDateChange}
          panelId='copy-range-date-section'
          sectionTitleId='copy-section-from'
          startDate={startDate}
          startDateString={startDateString}
        />
        {dateRangeError && <ErrorText role='alert'>{dateRangeError}</ErrorText>}
        {!dateRangeError && isSourceRange && totalDaysCount > 0 && (
          <HelperText>{pluralize(totalDaysCount, 'day')}</HelperText>
        )}
      </CopySection>
    );
  };

  const renderTargetDays = () => (
    <DaysBlock>
      <DaysHeaderRow>
        <DaysLabel>On these days</DaysLabel>
        <DaysSummary>
          {`${formatSelectedWeekdays(selectedDays) || 'No days selected'} · ${pluralize(targetDayCount, 'day')}`}
        </DaysSummary>
      </DaysHeaderRow>
      <CopyDaySelector
        onSetDays={setSelectedDays}
        onToggleDay={toggleDay}
        selectedDays={selectedDays}
      />
    </DaysBlock>
  );

  const renderPasteOnSection = () => {
    if (isProgramLibrary) {
      return (
        <CopySection id='copy-section-paste' title='Paste on'>
          <CopyProgramDayGrid
            idPrefix='copy-paste-day'
            onToggleDay={handlePasteProgramDayToggle}
            selectedKeys={pasteDestinationDays}
            weeks={programWeeks}
          />
        </CopySection>
      );
    }

    return (
      <CopySection
        headerControl={isSourceRange ? null : (
          <CopyDateModeToggle
            ariaLabel='Paste date mode'
            idPrefix='copy-target-tab'
            mode={secondSlideDateMode}
            onEnterSelect={handleTargetModeEnter}
            onSelect={setSecondSlideDateMode}
            panelId='copy-target-date-section'
          />
        )}
        id='copy-section-paste'
        title='Paste on'
      >
        <CopyDateInputs
          endDateString={secondSlideEndDateString}
          isTabPanel={!isSourceRange}
          labelledBy={isSourceRange ? 'copy-section-paste' : `copy-target-tab-${secondSlideDateMode}`}
          mode={effectiveTargetMode}
          onEndChange={handleSecondSlideEndDateChange}
          onPickerKeyDown={handleDatePickerKeyDown}
          onPickerKeyDownCapture={handleDatePickerKeyDownCapture}
          onStartChange={handleSecondSlideStartDateChange}
          panelId='copy-target-date-section'
          sectionTitleId='copy-section-paste'
          startDate={secondSlideStartDate}
          startDateString={secondSlideStartDateString}
        />
        {isSourceRange && secondSlideStartDateString && totalDaysCount > 0 && (
          <HelperText>
            {`Pastes ${pluralize(totalDaysCount, 'day')} starting ${formatShortDate(secondSlideStartDate)}.`}
          </HelperText>
        )}
        {effectiveTargetMode === 'range' && renderTargetDays()}
      </CopySection>
    );
  };

  const subtitle = getSubtitle();

  return (
    <ModalContainer
      data-nav-mode='mouse'
      onKeyDown={handleCheckboxEnter}
      ref={modalContainerRef}
    >
      <CloseButton aria-label='Close' onClick={closeModal} title='Close' type='button'>
        <IcomoonReact color={colors.textSecondary} icon='remove' iconSet={iconSet} size={14} />
      </CloseButton>
      <HeaderRow>
        <Title>{getTitle()}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </HeaderRow>

      {!(isProgramLibrary && shouldSkipFirstSlide) && renderFromSection()}

      {showTargetPicker && (
        <CopySection id='copy-section-to' title='To calendar'>
          <CopyTargetPicker
            calendarSelectAthletes={calendarSelectAthletes || []}
            checkedAthletes={checkedAthletes}
            checkedCalendars={checkedCalendars}
            isAthleteTarget={rightToggled}
            isLoading={isParentCalendarsLoading}
            onTargetTypeChange={setRightToggled}
            parentCalendars={parentCalendars || []}
            setCheckedAthletes={setCheckedAthletes}
            setCheckedCalendars={setCheckedCalendars}
            onCloseLockChange={setIsCloseLocked}
          />
        </CopySection>
      )}

      {renderPasteOnSection()}

      <CopySection id='copy-section-options' title='Options'>
        <CopyOptionsSection
          additionalOptions={additionalOptions}
          clearExistingWorkouts={clearExistingWorkouts}
          contentOptions={contentOptions}
          onPlacementChange={setPlacementOption}
          onToggleAdditionalOption={toggleAdditionalOption}
          onToggleClearExisting={handleToggleClearExisting}
          onToggleContentOption={toggleContentOption}
          placementOption={placementOption}
          placementOptions={PLACEMENT_OPTIONS}
          showItemTypes={!hasSelectedItems}
        />
      </CopySection>

      <FooterContainer data-modal-footer>
        {!!preSavedItemAssignedIds.length && dateMode !== 'range' && (
          <SelectedItemsWarning>
            <WarningIcon />
            {savedItemAssignedIds.length
              ? 'Some of the items selected are not saved. These items will not be copied.'
              : 'Selected items are not saved. You must select at least 1 saved item to copy.'}
          </SelectedItemsWarning>
        )}
        <ModalCtaButton
          disabled={isSubmitDisabled || isSubmitting}
          onClick={handleSubmitClick}
          primary
          type='button'
        >
          {getSubmitLabel()}
        </ModalCtaButton>
        <ModalCtaButton onClick={closeModal} type='button'>Cancel</ModalCtaButton>
      </FooterContainer>
    </ModalContainer>
  );
};

CopyWorkoutModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  setIsCloseLocked: PropTypes.func,
  modalData: PropTypes.shape({
    weekStartDate: PropTypes.instanceOf(Date),
    weekEndDate: PropTypes.instanceOf(Date),
    isFromWeekView: PropTypes.bool,
    currentViewedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    preSelectedItems: PropTypes.objectOf(PropTypes.shape({
      assignedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      date: PropTypes.string,
    })),
    skipFirstSlide: PropTypes.bool,
    sourceDayNumber: PropTypes.number,
    sourceWeekNumber: PropTypes.number,
  }),
};

CopyWorkoutModal.defaultProps = {
  modalData: null,
  setIsCloseLocked: () => {},
};

export default CopyWorkoutModal;
